import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Plus, Users, Compass, HelpCircle, Zap, X, Trophy, ChevronRight, Coffee, MessageCircle, LogOut, Heart } from 'lucide-react';
import api from '../config/api';

const Navbar = ({ user, onStartTour, sessionId, showXpModal = false, setShowXpModal, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCommunities = location.pathname === '/friends' || location.pathname.startsWith('/friends/');
  const navLink = (to, label, Icon, exact = false, dataTour = null) => {
    const isActive = exact ? location.pathname === to : (location.pathname === to || location.pathname.startsWith(to + '/'));
    return (
      <Link
        to={to}
        {...(dataTour ? { 'data-tour': dataTour } : {})}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[15px] font-medium transition-all ${
          isActive
            ? 'bg-gray-200/80 dark:bg-white/10 text-gray-900 dark:text-white'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} fill="none" />
        {label}
      </Link>
    );
  };
  const [clubExperience, setClubExperience] = useState([]);
  const [xpLoading, setXpLoading] = useState(false);

  useEffect(() => {
    if (showXpModal && user) {
      setXpLoading(true);
      api.get('/users/me/club-experience')
        .then(r => setClubExperience(r.data || []))
        .catch(() => setClubExperience([]))
        .finally(() => setXpLoading(false));
    }
  }, [showXpModal, user]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-[#1c1c22]/95 backdrop-blur-xl border-b border-gray-200/60 dark:border-[#3f3f46]/70 shadow-sm">
      <div className="flex items-center h-14 max-w-[1600px] mx-auto">
        <div className="pl-5 lg:pl-8">
          <Link
            to="/"
            className="flex items-center gap-2.5 group rounded-lg py-1.5 pr-2 -ml-2 transition-colors"
            data-tour="navbar-logo"
          >
            <img src="/favicon.svg" alt="" className="h-8 w-8 rounded-lg dark:invert transition-transform group-hover:scale-105" aria-hidden />
            <span className="text-[19px] font-semibold text-gray-900 dark:text-[#e5e5e5] tracking-tight group-hover:text-gray-700 dark:group-hover:text-white">
              ClubFinder Social
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-1 ml-auto pr-5 lg:pr-8">
          {user && onStartTour && (
            <button
              onClick={onStartTour}
              className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
              title="Take a tour"
              aria-label="Take a tour"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          )}
          <Link
            to="/hangouts"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[15px] font-medium transition-all ${
              location.pathname === '/hangouts' || location.pathname.startsWith('/hangouts/')
                ? 'bg-gray-200/80 dark:bg-white/10 text-gray-900 dark:text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
            }`}
          >
            <Heart className="h-5 w-5 shrink-0 text-pink-500 dark:text-pink-400" strokeWidth={1.5} fill="currentColor" />
            Discover Bonds
          </Link>
          <Link
            to="/reels"
            data-tour="navbar-reels"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[15px] font-medium transition-all ${
              location.pathname === '/reels' || location.pathname.startsWith('/reels/')
                ? 'bg-gray-200/80 dark:bg-white/10 text-gray-900 dark:text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
            }`}
          >
            <span className="w-5 h-5 shrink-0 rounded-md bg-gradient-to-br from-slate-600 to-blue-600 flex items-center justify-center">
              <Compass className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </span>
            ClubExplorer
          </Link>
          {isCommunities && (
            <Link
              to="/create-group"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-md hover:shadow-lg hover:scale-105"
              title="Create"
            >
              <Plus className="h-4 w-4" />
            </Link>
          )}
          {user ? (
            <>
              <Link
                to="/friends"
                data-tour="navbar-friends"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[15px] font-medium transition-all ${
                  location.pathname === '/friends' || location.pathname.startsWith('/friends/')
                    ? 'bg-gray-200/80 dark:bg-white/10 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                }`}
              >
                <span className="w-5 h-5 shrink-0 rounded-md bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                  <MessageCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                </span>
                Communities
              </Link>
              {navLink('/profile', 'Profile', User, true, 'navbar-profile')}
              <button
                type="button"
                onClick={() => { onLogout?.(); navigate('/'); }}
                className="p-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                aria-label="Log out"
                title="Log out"
              >
                <LogOut className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="ml-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-all font-medium shadow-sm"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>

      {/* XP / Club Xp Modal - rendered via portal */}
      {showXpModal && setShowXpModal && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowXpModal && setShowXpModal(false)}
        >
          <div
            className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl bg-white dark:bg-[#1e1e24] shadow-2xl border border-gray-200/80 dark:border-[#3f3f46]"
            onClick={e => e.stopPropagation()}
            style={{ margin: 'max(1rem, env(safe-area-inset-top))' }}
          >
            {/* Header with gradient accent */}
            <div className="relative overflow-hidden px-5 py-4 border-b border-gray-200/80 dark:border-[#3f3f46]">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent dark:from-amber-500/15 dark:via-orange-500/10" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/20 dark:bg-amber-500/25">
                    <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                      Club Xp
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Track your progress in each club
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowXpModal && setShowXpModal(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(85vh-8rem)]">
              {xpLoading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 rounded-xl bg-gray-100 dark:bg-[#2a2a32] animate-pulse" />
                  ))}
                </div>
              ) : clubExperience.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 dark:bg-amber-500/15 mb-4">
                    <Trophy className="h-8 w-8 text-amber-600/70 dark:text-amber-400/70" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Start earning XP
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm max-w-xs mx-auto mb-4">
                    Join clubs and attend events to level up. You earn <span className="font-medium text-amber-600 dark:text-amber-400">10 XP</span> per club and <span className="font-medium text-amber-600 dark:text-amber-400">5 XP</span> per event.
                  </p>
                  <button
                    onClick={() => { setShowXpModal && setShowXpModal(false); navigate('/'); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm transition-colors shadow-lg shadow-amber-500/25"
                  >
                    Explore clubs
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="p-4 space-y-3">
                  {[...clubExperience]
                    .sort((a, b) => (b.xp || 0) - (a.xp || 0))
                    .map(c => {
                      const xpInLevel = (c.xp ?? 0) % 20;
                      const progress = (xpInLevel / 20) * 100;
                      return (
                        <Link
                          key={c.id}
                          to={`/groups/${c.id}`}
                          onClick={() => setShowXpModal && setShowXpModal(false)}
                          className="group flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 dark:bg-[#25252c] hover:bg-gray-100 dark:hover:bg-[#2d2d36] border border-gray-100 dark:border-[#3f3f46]/80 transition-all"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-white truncate">{c.name}</p>
                            {c.category && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{c.category}</p>
                            )}
                            <div className="mt-2 h-1.5 w-full max-w-[140px] rounded-full bg-gray-200 dark:bg-[#3f3f46] overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-amber-500/20 dark:bg-amber-500/25 text-amber-700 dark:text-amber-300">
                              Lvl {c.level ?? 1}
                            </span>
                            <div className="text-right">
                              <p className="font-bold text-amber-600 dark:text-amber-400">{c.xp ?? 0} XP</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{c.eventsAttended ?? 0} events</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors" />
                          </div>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>

            {clubExperience.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-200/80 dark:border-[#3f3f46] bg-gray-50/50 dark:bg-[#25252c]/80">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {clubExperience.length} club{clubExperience.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
                    {clubExperience.reduce((s, c) => s + (c.xp ?? 0), 0)} total XP
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navbar;
